import React from 'react';
import NProgress from 'nprogress';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { z, ZodError } from 'zod';
import { FirebaseError } from 'firebase/app';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { firestore, loadAnalytics, storage, uploadFile } from '../../../lib';
import { Button } from '../../commons';
import { DropArea } from '../../commons/drag-and-drop';
import { RadioCheckInput } from '../../inputs';
import {
  Application,
  Office,
  Service,
  Volunteering,
  volunteeringSchema,
} from '../../../models';
import LeadershipForm from './LeadershipForm';
import ClubForm from './ClubForm';
import ServiceForm from './ServiceForm';

interface VolunteerFormProps {
  volunteer: Volunteering;
  application: Application;
  isReadOnly?: boolean;
}

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

export default function VolunteeringForm({
  application,
  volunteer,
  isReadOnly = false,
}: VolunteerFormProps) {
  const router = useRouter();
  const [leadership, setLeadership] = React.useState<Office[]>([]);
  const [clubs, setClubs] = React.useState<Office[]>([]);
  const [services, setServices] = React.useState<Service[]>([]);
  const [file, setFile] = React.useState<File | null>(null);

  React.useEffect(
    function syncClubs() {
      if (Array.isArray(volunteer?.clubs)) {
        setClubs(volunteer?.clubs ?? []);
      }
    },
    [volunteer?.clubs]
  );

  React.useEffect(
    function syncLeadership() {
      if (Array.isArray(volunteer?.leadership)) {
        setClubs(volunteer?.leadership ?? []);
      }
    },
    [volunteer?.leadership]
  );

  React.useEffect(
    function syncServices() {
      if (Array.isArray(volunteer?.services)) {
        setServices(volunteer?.services ?? []);
      }
    },
    [volunteer?.services]
  );

  const onDrop = React.useCallback((acceptedFiles: File[]): void => {
    const [_file] = acceptedFiles;
    if (_file.size > 10000000) {
      return console.error('Mas 10MB');
    }
    setFile(_file);
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    let data: z.infer<typeof volunteeringSchema> = null;
    try {
      data = volunteeringSchema.parse({
        ...Object.fromEntries(formData.entries()),
        leadership,
        clubs,
        services,
      });
    } catch (err) {
      console.log(err);
      if (err instanceof ZodError) {
        return toastError(err.issues[0].message);
      }
      return toastError(
        'An error has occurred, please try again later. CODE: E71'
      );
    }
    let resume = volunteer?.resume ?? '';
    try {
      NProgress.start();
      if (file) {
        resume = await uploadFile(
          storage,
          file,
          `/applications/${application?.id}/resume`
        );
      }
    } catch (err) {
      NProgress.done();
      posthog?.captureException(err);
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'storage/quota-exceeded':
            return toastError(
              'Error uploading transcript file, check your internet. Please contact support with this CODE: storage/quota-exceeded'
            );
          case 'storage/retry-limit-exceeded':
          case 'storage/invalid-checksum':
          case 'storage/canceled':
          case 'storage/invalid-argument':
            return toastError(
              'Error uploading transcript file, your internet is unstable. FE72.1'
            );
          default:
            return toastError(
              'Error uploading transcript file, check your internet. CODE: FE72'
            );
        }
      }
      return toastError(
        'Error uploading transcript file, check your internet. CODE: E72'
      );
    }
    try {
      await updateDoc(doc(firestore, 'applications', application?.id), {
        lastUpdated: Timestamp.fromDate(new Date()),
        step: '7',
        volunteering: {
          ...data,
          resume,
        },
      });

      logEvent(loadAnalytics(), 'step_completed', {
        step: '7',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '7',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        '/request-scholarship/8'
      );
    } catch (err) {
      NProgress.done();
      posthog?.captureException(err);
      Sentry.addBreadcrumb({ level: 'error', category: 'step-7' });
      Sentry.captureException(err);
      toastError('An error has occurred, please try again later. CODE: E73');
    }
  };
  return (
    <form method="POST" onSubmit={onSubmit} className="mt-8">
      {/* Leadership */}
      <LeadershipForm
        leadership={leadership}
        setLeadership={setLeadership}
        isReadOnly={isReadOnly}
      />
      {/* Clubs */}
      <ClubForm clubs={clubs} setClubs={setClubs} isReadOnly={isReadOnly} />
      <div className="mt-8">
        <p className="text-gray-500 text-[14px] font-bold mb-2">
          Have you participated in community / voluntary service?
        </p>
        <div className="flex">
          <RadioCheckInput
            type="radio"
            label="Yes"
            value="yes"
            name="voluntaryService"
            labelClassName="font-bold text-[20px] text-neutral-500"
            className="mb-2 mr-4"
            readOnly={isReadOnly}
            disabled={isReadOnly}
            defaultChecked={volunteer?.voluntaryService === 'yes'}
          />
          <RadioCheckInput
            type="radio"
            label="No"
            value="no"
            name="voluntaryService"
            labelClassName="font-bold text-[20px] text-neutral-500"
            className="mb-2"
            readOnly={isReadOnly}
            disabled={isReadOnly}
            defaultChecked={volunteer?.voluntaryService === 'no'}
          />
        </div>
      </div>
      <div className="mt-8">
        <p className="text-neutral-600">
          If yes, want kind of assistance did you provide and when?
        </p>
        <ServiceForm
          isReadOnly={isReadOnly}
          services={services}
          setServices={setServices}
        />
      </div>
      <div className="mt-8">
        {isReadOnly ? null : (
          <DropArea
            label={file ? file.name : 'Kindly upload your resume'}
            document="resume"
            override={!!file}
            onDrop={onDrop}
            accept={{
              // 'image/*': ['.jpeg', '.jpg', '.png'],
              'application/pdf': ['.pdf'],
            }}
          />
        )}
        {volunteer?.resume ? (
          <a
            href={volunteer?.resume}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] text-neutral-400 mb-8"
          >
            Preview File (Resume)
          </a>
        ) : null}
      </div>
      {isReadOnly ? null : (
        <div className="mt-8 flex">
          <Button type="submit" color="blue" className="ml-auto w-[135px]">
            Continue
          </Button>
        </div>
      )}
    </form>
  );
}
